import { MapPin, Calendar, IndianRupee, Clock, CheckCircle2, Circle, PlayCircle, Plus, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrency } from "@/lib/format";

const getStageIcon = (status) => {
  switch (status) {
    case 'completed': return <CheckCircle2 className="w-5 h-5 text-[#10B981]" />;
    case 'in_progress': return <PlayCircle className="w-5 h-5 text-[#FF4500]" />;
    default: return <Circle className="w-5 h-5 text-gray-600" />;
  }
};

export const OverviewTab = ({ project, emailConfigured, handleStageUpdate, handlePaymentUpdate, onRemindPayment, onScheduleVisit }) => (
  <TabsContent value="overview" className="space-y-6 animate-fade-in">
    {/* Project Stages */}
    <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6">
      <h3 className="text-lg font-semibold text-white mb-6">Project Stages</h3>

      {/* Visual Stage Progress */}
      <div className="flex gap-2 mb-8">
        {project.stages?.map((stage, idx) => (
          <div
            key={idx}
            className={`relative flex-1 p-4 rounded cursor-pointer transition-all interactive ${
              stage.status === 'completed' ? 'bg-[#10B981]/20 border-2 border-[#10B981]/40' :
              stage.status === 'in_progress' ? 'bg-[#FF4500]/20 border-2 border-[#FF4500]/40 animate-pulse-glow' :
              'bg-[#262626] border-2 border-[#333] hover:border-[#404040]'
            }`}
            onClick={() => {
              if (stage.status === 'pending') handleStageUpdate(stage.stage_number, 'in_progress');
              else if (stage.status === 'in_progress') handleStageUpdate(stage.stage_number, 'completed');
            }}
            data-testid={`stage-${stage.stage_number}`}
          >
            <span className={`absolute top-2 right-2 text-3xl font-black ${
              stage.status === 'completed' ? 'text-[#10B981]/30' :
              stage.status === 'in_progress' ? 'text-[#FF4500]/30' :
              'text-gray-700'
            }`}>
              {String(stage.stage_number).padStart(2, '0')}
            </span>
            <div className="mt-8">
              <p className={`text-sm font-medium ${
                stage.status === 'completed' ? 'text-[#10B981]' :
                stage.status === 'in_progress' ? 'text-[#FF4500]' :
                'text-gray-400'
              }`}>
                {stage.stage_name}
              </p>
              <p className="text-xs text-gray-500 mt-1 capitalize">{stage.status?.replace('_', ' ')}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Detailed Stage List */}
      <div className="space-y-3">
        {project.stages?.map((stage, idx) => (
          <div key={idx} className="flex items-center gap-4 p-4 bg-[#171717] rounded border border-[#262626] hover:border-[#404040] transition-colors">
            {getStageIcon(stage.status)}
            <div className="flex-1">
              <p className="font-medium text-white">{stage.stage_name}</p>
              {stage.deliverables && (
                <p className="text-xs text-gray-500 mt-1">{stage.deliverables.join(', ')}</p>
              )}
            </div>
            <div className="text-right">
              {stage.payment_percent > 0 && (
                <p className="text-sm text-[#FF4500] font-medium">{stage.payment_percent}%</p>
              )}
            </div>
            <Select value={stage.status} onValueChange={(value) => handleStageUpdate(stage.stage_number, value)}>
              <SelectTrigger className="w-[120px] bg-[#262626] border-[#333]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </div>

    <div className="grid grid-cols-12 gap-6">
      {/* Payments */}
      <div className="col-span-12 lg:col-span-6">
        <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-[#FF4500]" /> Payments
            </h3>
            {emailConfigured && (
              <Button size="sm" variant="outline" onClick={onRemindPayment} className="border-[#262626]">
                <Send className="w-3 h-3 mr-1" /> Remind
              </Button>
            )}
          </div>

          {project.payments?.length > 0 ? (
            <div className="space-y-3">
              {project.payments.map((payment, idx) => (
                <div key={payment.payment_id || idx} className="flex items-center justify-between p-3 bg-[#171717] rounded border border-[#262626] hover:border-[#404040] transition-colors">
                  <div>
                    <p className="text-sm font-medium text-white">{payment.description}</p>
                    <p className="text-xs text-gray-500">Stage {payment.stage_number}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-sm font-semibold text-white">{formatCurrency(payment.amount)}</p>
                    <Select value={payment.status} onValueChange={(value) => handlePaymentUpdate(payment.payment_id, value)}>
                      <SelectTrigger className={`w-[100px] ${
                        payment.status === 'paid' ? 'bg-[#10B981]/20 border-[#10B981]/40 text-[#10B981]' :
                        payment.status === 'overdue' ? 'bg-red-500/20 border-red-500/40 text-red-400' :
                        'bg-[#262626] border-[#333]'
                      }`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="overdue">Overdue</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No payments scheduled</p>
          )}
        </div>
      </div>

      {/* Site Visits */}
      <div className="col-span-12 lg:col-span-6">
        <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#F59E0B]" /> Site Visits
              <span className="text-xs text-gray-500 font-normal">({project.site_visits_used}/{project.site_visits_included})</span>
            </h3>
            <Button size="sm" variant="outline" onClick={onScheduleVisit} className="border-[#262626]">
              <Plus className="w-3 h-3" />
            </Button>
          </div>

          {project.site_visits?.length > 0 ? (
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {project.site_visits.map((visit, idx) => (
                <div key={visit.visit_id || idx} className="p-3 bg-[#171717] rounded border border-[#262626]">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-white">{visit.purpose}</p>
                    <span className={`badge ${visit.status === 'completed' ? 'status-completed' : visit.status === 'scheduled' ? 'status-pending' : 'bg-gray-700 text-gray-300'}`}>
                      {visit.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{visit.scheduled_date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{visit.scheduled_time}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No visits scheduled</p>
          )}
        </div>
      </div>
    </div>
  </TabsContent>
);
